import React from "react";
import { useNavigate } from "react-router-dom";
import "./AboutPage.scss";

function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="about-page">
      <nav className="about-nav">
        <button className="back-button" onClick={() => navigate("/")}>
          ← Back
        </button>
        <span className="about-nav-title">Embodied Intelligence</span>
      </nav>

      <main className="about-content">
        <section className="about-intro">
          <h1 className="about-title">About</h1>
          <p className="about-lead">
            What if using AI felt as costly as it actually is?
          </p>
          <p className="about-body">
            Embodied Intelligence is an interactive web experience that makes
            the hidden environmental footprint of AI visible and tangible, using
            nothing but a browser and your hands. Open your webcam. Type your AI
            question. Then crank. The site uses hand-tracking to detect your
            motion, and you don't get your answer until you've put in the
            physical effort equivalent to the energy that query actually
            consumes. No cranking, no answer. When you're done, you get more
            than a response — you get the receipt: a full breakdown of the
            electricity burned, the water evaporated for server cooling, and the
            carbon released into the atmosphere. The gap between how easy AI
            feels and what it actually takes is the whole point.
          </p>
        </section>

        <section className="about-iterations">
          <h2 className="iterations-heading">Previous Iterations</h2>

          <div className="iteration">
            <div className="iteration-marker">01</div>
            <div className="iteration-body">
              <h3 className="iteration-title">
                Sweat for Generation
                <span className="iteration-year">2025</span>
              </h3>
              <p className="iteration-description">
                The project began as a physical installation where participants
                turned a hand crank to generate a single AI query — hours of
                sustained human exertion mapped onto seconds of computation.
                Sweat, muscle strain, and exhaustion made the energy economy of
                data centers, global supply chains, and mineral extraction
                impossible to ignore.
              </p>
              <a
                href="https://threshold-lab.cargo.site/"
                target="_blank"
                rel="noopener noreferrer"
                className="iteration-link"
              >
                threshold-lab.cargo.site ↗
              </a>
            </div>
          </div>

          <div className="iteration">
            <div className="iteration-marker">02</div>
            <div className="iteration-body">
              <h3 className="iteration-title">
                Self-contained Installation
                <span className="iteration-year">2025</span>
              </h3>
              <p className="iteration-description">
                The second iteration brought the concept into a self-contained
                installation with a printed receipt system. Participants cranked
                a handle, received their AI-generated answer on a thermal
                receipt alongside its energy, water, and carbon costs, and could
                scan a QR code for a deeper breakdown and tips for reducing
                impact. The hardware was deliberately low-resource — 3D-printed
                from recycled filament, powered by Arduino and ESP32
                microcontrollers, and driven entirely by hand-powered rotary
                encoders — so the installation itself practiced the
                sustainability it asked visitors to consider.
              </p>
              <a
                href="https://embodied-intelligence.cargo.site/"
                target="_blank"
                rel="noopener noreferrer"
                className="iteration-link"
              >
                embodied-intelligence.cargo.site ↗
              </a>
            </div>
          </div>

          <div className="iteration">
            <div className="iteration-marker">03</div>
            <div className="iteration-body">
              <h3 className="iteration-title">
                Web Version
                <span className="iteration-year">2025 — present</span>
              </h3>
              <p className="iteration-description">
                The current web version carries that same provocation to anyone
                with a laptop and a webcam, removing the need for gallery space
                or custom hardware while preserving the core exchange: effort
                in, awareness out.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default AboutPage;
